import { CUSTOM_TRACKING_CATEGORY, DEFAULT_CUSTOM_COLUMNS } from './collegeColumns';

function trackingColumn(id, label, customType = 'text', labels = []) {
  const hasOptions = customType === 'label' || customType === 'dropdown';

  return {
    id,
    label,
    category: CUSTOM_TRACKING_CATEGORY,
    type: 'custom',
    isBuiltIn: true,
    customType,
    labels: hasOptions ? labels : [],
    sortable: true,
    sortType: customType === 'number' ? 'number' : 'string',
  };
}

const WORKFLOW_STATUSES = ['Not Started', 'In Progress', 'Complete'];
const SCORE_SEND_STATUSES = ['Not needed', 'Need to send', 'Sent', 'Confirmed received'];

function widthsFor(activeColumns, overrides = {}) {
  return activeColumns.reduce((widths, columnId) => ({
    ...widths,
    [columnId]: overrides[columnId] || 180,
  }), {});
}

const commonWidths = {
  checkbox: 96,
  school_name: 230,
  category: 250,
  date: 150,
  acceptance_rate: 170,
  city: 150,
  state: 120,
  distance_from_home: 210,
  sat_range: 140,
  act_range: 140,
  cost_attendance: 150,
  test_optional: 150,
};

const applicationColumns = [
  trackingColumn('custom_application_status', 'Status', 'label', ['Not Started', 'In Progress', 'Submitted', 'Accepted', 'Waitlisted', 'Denied']),
  trackingColumn('custom_sat_scores_sent', 'SAT Scores Sent', 'label', SCORE_SEND_STATUSES),
  trackingColumn('custom_act_scores_sent', 'ACT Scores Sent', 'label', SCORE_SEND_STATUSES),
  trackingColumn('custom_application_notes', 'Application Notes'),
  trackingColumn('custom_application_link', 'Application Link', 'link'),
];

const tourColumns = [
  trackingColumn('custom_tour_status', 'Status', 'label', WORKFLOW_STATUSES),
  trackingColumn('custom_tour_date', 'Tour Date', 'date'),
  trackingColumn('custom_tour_type', 'Tour Type', 'label', ['In person', 'Virtual', 'Self-guided', 'Info session']),
  trackingColumn('custom_tour_link', 'Tour Link', 'link'),
  trackingColumn('custom_admissions_contact', 'Admissions Contact'),
  trackingColumn('custom_travel_notes', 'Travel Notes'),
  trackingColumn('custom_campus_feel', 'Campus Feel', 'label', ['Loved it', 'Liked it', 'Unsure', 'Not a fit']),
  trackingColumn('custom_academics_notes', 'Academics'),
  trackingColumn('custom_housing_notes', 'Housing'),
  trackingColumn('custom_student_life_notes', 'Student Life'),
  trackingColumn('custom_pros', 'Pros'),
  trackingColumn('custom_cons', 'Cons'),
  trackingColumn('custom_questions', 'Questions'),
];

const scholarshipColumns = [
  trackingColumn('custom_scholarship_status', 'Status', 'label', WORKFLOW_STATUSES),
  trackingColumn('custom_scholarship_name', 'Scholarship'),
  trackingColumn('custom_scholarship_link', 'Scholarship Link', 'link'),
  trackingColumn('custom_scholarship_amount', 'Amount', 'number'),
  trackingColumn('custom_scholarship_deadline', 'Deadline', 'date'),
  trackingColumn('custom_scholarship_requirements', 'Requirements'),
];

const essayColumns = [
  trackingColumn('custom_essay_status', 'Status', 'label', WORKFLOW_STATUSES),
  trackingColumn('custom_essay_prompt', 'Prompt'),
  trackingColumn('custom_essay_link', 'Essay Link', 'link'),
  trackingColumn('custom_word_limit', 'Word Limit', 'number'),
  trackingColumn('custom_draft_status', 'Draft', 'label', WORKFLOW_STATUSES),
  trackingColumn('custom_final_status', 'Final', 'label', WORKFLOW_STATUSES),
  trackingColumn('custom_submission_status', 'Submitted', 'label', WORKFLOW_STATUSES),
];

const contactColumns = [
  trackingColumn('custom_contact_status', 'Status', 'label', WORKFLOW_STATUSES),
  trackingColumn('custom_contact_name', 'Contact Name'),
  trackingColumn('custom_contact_email', 'Email'),
  trackingColumn('custom_contact_link', 'Contact Link', 'link'),
  trackingColumn('custom_date_contacted', 'Contacted', 'date'),
  trackingColumn('custom_contact_topic', 'Topic'),
];

const decisionColumns = [
  trackingColumn('custom_decision_status', 'Status', 'label', WORKFLOW_STATUSES),
  trackingColumn('custom_decision', 'Decision', 'label', ['Accepted', 'Waitlisted', 'Rejected', 'Deferred', 'Pending']),
  trackingColumn('custom_decision_date', 'Decision Date', 'date'),
  trackingColumn('custom_decision_link', 'Decision Link', 'link'),
  trackingColumn('custom_aid_offer', 'Financial Aid Offer', 'number'),
  trackingColumn('custom_merit_scholarship', 'Merit Scholarship', 'number'),
  trackingColumn('custom_deposit_deadline', 'Deposit Deadline', 'date'),
  trackingColumn('custom_deposit_status', 'Deposit', 'label', WORKFLOW_STATUSES),
  trackingColumn('custom_enrollment_intent', 'Enrollment Intent', 'label', ['Committed', 'Considering', 'Declined']),
  trackingColumn('custom_decision_notes', 'Notes'),
];

const cssFafsaColumns = [
  trackingColumn('custom_aid_status', 'Aid Status', 'label', ['Not Started', 'In Progress', 'Submitted', 'Received']),
  trackingColumn('custom_fafsa_code', 'FAFSA Code'),
  trackingColumn('custom_css_code', 'CSS Code'),
  trackingColumn('custom_fafsa_link', 'FAFSA Link', 'link'),
  trackingColumn('custom_css_link', 'CSS Link', 'link'),
  trackingColumn('custom_fafsa_status', 'FAFSA Status', 'label', WORKFLOW_STATUSES),
  trackingColumn('custom_css_status', 'CSS Status', 'label', WORKFLOW_STATUSES),
  trackingColumn('custom_fafsa_deadline', 'FAFSA Deadline', 'date'),
  trackingColumn('custom_css_deadline', 'CSS Deadline', 'date'),
  trackingColumn('custom_css_required', 'CSS Required', 'label', ['Yes', 'No', 'Recommended']),
  trackingColumn('custom_efc', 'EFC / SAI'),
  trackingColumn('custom_aid_notes', 'Notes'),
];

export const DASHBOARD_TEMPLATES = [
  {
    id: 'searching',
    name: 'Searching Process',
    description: 'Compare fit, cost, scores, and admissions data while you are building your list.',
    previewColumns: ['School', 'Acceptance', 'Location', 'SAT', 'ACT', 'Cost'],
    activeColumns: [
      'school_name',
      'school_url',
      'category',
      'acceptance_rate',
      'city',
      'state',
      'distance_from_home',
      'sat_range',
      'act_range',
      'cost_attendance',
      'test_optional',
    ],
    customColumns: DEFAULT_CUSTOM_COLUMNS,
    columnWidths: widthsFor([
      'school_name',
      'school_url',
      'category',
      'acceptance_rate',
      'city',
      'state',
      'distance_from_home',
      'sat_range',
      'act_range',
      'cost_attendance',
      'test_optional',
    ], {
      ...commonWidths,
      school_url: 150,
    }),
  },
  {
    id: 'costs',
    name: 'Costs & Aid',
    description: 'A financial view with tuition, net price, room and board, grants, loans, and debt data from the college API.',
    previewColumns: ['School', 'Tuition', 'Net Price', 'Pell', 'Loans', 'Debt'],
    activeColumns: [
      'school_name',
      'cost_attendance',
      'cost_in_state',
      'cost_out_state',
      'avg_net_price',
      'room_board_oncampus',
      'books_supplies',
      'pell_grant_rate',
      'federal_loan_rate',
      'any_loan_rate',
      'median_debt',
    ],
    customColumns: DEFAULT_CUSTOM_COLUMNS,
    columnWidths: widthsFor([
      'school_name',
      'cost_attendance',
      'cost_in_state',
      'cost_out_state',
      'avg_net_price',
      'room_board_oncampus',
      'books_supplies',
      'pell_grant_rate',
      'federal_loan_rate',
      'any_loan_rate',
      'median_debt',
    ], {
      ...commonWidths,
      cost_in_state: 170,
      cost_out_state: 190,
      avg_net_price: 175,
      room_board_oncampus: 175,
      books_supplies: 170,
      pell_grant_rate: 155,
      federal_loan_rate: 175,
      any_loan_rate: 155,
      median_debt: 150,
    }),
  },
  {
    id: 'tours',
    name: 'College Tours',
    description: 'Track visits, campus feel, travel details, contacts, and the notes you do not want to lose.',
    previewColumns: ['School', 'Tour Date', 'Type', 'Feel', 'Pros', 'Questions'],
    activeColumns: [
      'school_name',
      'custom_tour_date',
      'custom_tour_type',
      'custom_tour_status',
      'custom_tour_link',
      'custom_campus_feel',
      'custom_admissions_contact',
      'custom_travel_notes',
      'custom_academics_notes',
      'custom_housing_notes',
      'custom_student_life_notes',
      'custom_pros',
      'custom_cons',
      'custom_questions',
    ],
    customColumns: [...DEFAULT_CUSTOM_COLUMNS, ...tourColumns],
    columnWidths: widthsFor([
      'school_name',
      'custom_tour_date',
      'custom_tour_type',
      'custom_tour_status',
      'custom_tour_link',
      'custom_campus_feel',
      'custom_admissions_contact',
      'custom_travel_notes',
      'custom_academics_notes',
      'custom_housing_notes',
      'custom_student_life_notes',
      'custom_pros',
      'custom_cons',
      'custom_questions',
    ], {
      ...commonWidths,
      custom_tour_date: 150,
      custom_tour_type: 150,
      custom_tour_status: 150,
      custom_tour_link: 150,
      custom_campus_feel: 160,
      custom_admissions_contact: 210,
      custom_student_life_notes: 190,
      custom_questions: 200,
    }),
  },
  {
    id: 'applications',
    name: 'Application Tracker',
    description: 'A practical setup for deadlines, rank, portals, notes, and final submission checks.',
    previewColumns: ['Status', 'School', 'Rank', 'Deadline', 'Portal'],
    activeColumns: [
      'custom_application_status',
      'school_name',
      'category',
      'date',
      'custom_application_link',
      'custom_portal_login',
      'custom_sat_scores_sent',
      'custom_act_scores_sent',
      'custom_application_notes',
      'custom_scholarship_notes',
    ],
    customColumns: [...DEFAULT_CUSTOM_COLUMNS, ...applicationColumns],
    columnWidths: widthsFor([
      'custom_application_status',
      'school_name',
      'category',
      'date',
      'custom_application_link',
      'custom_portal_login',
      'custom_sat_scores_sent',
      'custom_act_scores_sent',
      'custom_application_notes',
      'custom_scholarship_notes',
    ], {
      ...commonWidths,
      custom_application_status: 170,
      custom_application_link: 160,
      custom_portal_login: 170,
      custom_sat_scores_sent: 170,
      custom_act_scores_sent: 170,
      custom_application_notes: 210,
      custom_scholarship_notes: 210,
    }),
  },
  {
    id: 'scholarships',
    name: 'Scholarship Tracker',
    description: 'Track each scholarship, amount, deadline, requirements, and whether it has been submitted.',
    previewColumns: ['Status', 'School', 'Scholarship', 'Amount', 'Deadline'],
    activeColumns: [
      'custom_scholarship_status',
      'school_name',
      'custom_scholarship_name',
      'custom_scholarship_link',
      'custom_scholarship_amount',
      'custom_scholarship_deadline',
      'custom_scholarship_requirements',
    ],
    customColumns: [...DEFAULT_CUSTOM_COLUMNS, ...scholarshipColumns],
    columnWidths: widthsFor([
      'custom_scholarship_status',
      'school_name',
      'custom_scholarship_name',
      'custom_scholarship_link',
      'custom_scholarship_amount',
      'custom_scholarship_deadline',
      'custom_scholarship_requirements',
    ], {
      ...commonWidths,
      custom_scholarship_status: 145,
      custom_scholarship_name: 190,
      custom_scholarship_link: 160,
      custom_scholarship_amount: 140,
      custom_scholarship_deadline: 150,
      custom_scholarship_requirements: 220,
    }),
  },
  {
    id: 'essays',
    name: 'Essay Tracker',
    description: 'Track prompts, word limits, drafts, final review, and submission status.',
    previewColumns: ['Status', 'School', 'Prompt', 'Link', 'Draft', 'Final'],
    activeColumns: [
      'custom_essay_status',
      'school_name',
      'custom_essay_prompt',
      'custom_essay_link',
      'custom_word_limit',
      'custom_draft_status',
      'custom_final_status',
      'custom_submission_status',
    ],
    customColumns: [...DEFAULT_CUSTOM_COLUMNS, ...essayColumns],
    columnWidths: widthsFor([
      'custom_essay_status',
      'school_name',
      'custom_essay_prompt',
      'custom_essay_link',
      'custom_word_limit',
      'custom_draft_status',
      'custom_final_status',
      'custom_submission_status',
    ], {
      ...commonWidths,
      custom_essay_status: 145,
      custom_essay_link: 150,
      custom_draft_status: 130,
      custom_final_status: 120,
      custom_submission_status: 145,
      custom_essay_prompt: 260,
      custom_word_limit: 130,
    }),
  },
  {
    id: 'contacts',
    name: 'Admissions Contacts',
    description: 'Keep track of who you contacted, when you reached out, and what you asked about.',
    previewColumns: ['School', 'Name', 'Email', 'Date', 'Topic'],
    activeColumns: [
      'custom_contact_status',
      'school_name',
      'custom_contact_name',
      'custom_contact_email',
      'custom_contact_link',
      'custom_date_contacted',
      'custom_contact_topic',
    ],
    customColumns: [...DEFAULT_CUSTOM_COLUMNS, ...contactColumns],
    columnWidths: widthsFor([
      'custom_contact_status',
      'school_name',
      'custom_contact_name',
      'custom_contact_email',
      'custom_contact_link',
      'custom_date_contacted',
      'custom_contact_topic',
    ], {
      ...commonWidths,
      custom_contact_status: 145,
      custom_contact_name: 180,
      custom_contact_email: 220,
      custom_contact_link: 150,
      custom_date_contacted: 160,
      custom_contact_topic: 220,
    }),
  },
  {
    id: 'decisions',
    name: 'Decision Tracker',
    description: 'Track acceptances, waitlists, rejections, financial aid offers, deposits, and enrollment intent.',
    previewColumns: ['School', 'Decision', 'Aid Offer', 'Deposit', 'Intent'],
    activeColumns: [
      'custom_decision_status',
      'school_name',
      'custom_decision',
      'custom_decision_date',
      'custom_decision_link',
      'custom_aid_offer',
      'custom_merit_scholarship',
      'custom_deposit_deadline',
      'custom_deposit_status',
      'custom_enrollment_intent',
      'custom_decision_notes',
    ],
    customColumns: [...DEFAULT_CUSTOM_COLUMNS, ...decisionColumns],
    columnWidths: widthsFor([
      'custom_decision_status',
      'school_name',
      'custom_decision',
      'custom_decision_date',
      'custom_decision_link',
      'custom_aid_offer',
      'custom_merit_scholarship',
      'custom_deposit_deadline',
      'custom_deposit_status',
      'custom_enrollment_intent',
      'custom_decision_notes',
    ], {
      ...commonWidths,
      custom_decision_status: 145,
      custom_decision: 160,
      custom_decision_date: 160,
      custom_decision_link: 150,
      custom_aid_offer: 180,
      custom_merit_scholarship: 180,
      custom_deposit_deadline: 170,
      custom_deposit_status: 140,
      custom_enrollment_intent: 180,
      custom_decision_notes: 220,
    }),
  },
  {
    id: 'css-fafsa',
    name: 'CSS & FAFSA',
    description: 'Track FAFSA and CSS Profile codes, deadlines, submission status, and financial aid progress.',
    previewColumns: ['Aid Status', 'School', 'FAFSA', 'CSS', 'Deadlines'],
    activeColumns: [
      'custom_aid_status',
      'school_name',
      'custom_fafsa_code',
      'custom_css_code',
      'custom_fafsa_link',
      'custom_css_link',
      'custom_fafsa_deadline',
      'custom_css_deadline',
      'custom_fafsa_status',
      'custom_css_status',
      'custom_css_required',
      'custom_efc',
      'custom_aid_notes',
    ],
    customColumns: [...DEFAULT_CUSTOM_COLUMNS, ...cssFafsaColumns],
    columnWidths: widthsFor([
      'custom_aid_status',
      'school_name',
      'custom_fafsa_code',
      'custom_css_code',
      'custom_fafsa_link',
      'custom_css_link',
      'custom_fafsa_deadline',
      'custom_css_deadline',
      'custom_fafsa_status',
      'custom_css_status',
      'custom_css_required',
      'custom_efc',
      'custom_aid_notes',
    ], {
      ...commonWidths,
      custom_fafsa_code: 150,
      custom_css_code: 140,
      custom_fafsa_link: 150,
      custom_css_link: 150,
      custom_fafsa_deadline: 165,
      custom_css_deadline: 155,
      custom_fafsa_status: 145,
      custom_css_status: 145,
      custom_css_required: 160,
      custom_aid_status: 155,
      custom_efc: 140,
      custom_aid_notes: 220,
    }),
  },
];

export const FINAL_LIST_PREFILL_TEMPLATE_IDS = [
  'searching',
  'costs',
  'tours',
  'applications',
  'contacts',
  'decisions',
  'css-fafsa',
];

export const CUSTOM_DASHBOARD_TEMPLATE = {
  id: 'custom',
  name: 'Start from Scratch',
  description: 'Make your own dashboard and add only the fields you want.',
  previewColumns: ['School', 'Rank', 'Deadline'],
  activeColumns: ['school_name', 'category', 'date'],
  customColumns: DEFAULT_CUSTOM_COLUMNS,
  columnWidths: widthsFor(['school_name', 'category', 'date'], commonWidths),
};

export function getDashboardTemplate(templateId) {
  return DASHBOARD_TEMPLATES.find((template) => template.id === templateId) || CUSTOM_DASHBOARD_TEMPLATE;
}
